/**
 * The single error-alert surface for admin forms - so each form renders ONE
 * component for both kinds of failure rather than hand-rolling its own
 * `<Alert>`s. It is the plain app-standard `<Alert severity="error">` (same as
 * every other alert bar in the app), enhanced with:
 *  - `message` - a server/single error string (e.g. a failed save), and/or
 *  - `errors` - a `field -> message` map rendered as a dashed list. When a
 *    field is mapped to a tab (and/or a DOM anchor) its entry is clickable and
 *    jumps to that tab, then focuses/scrolls the field. Empty-string messages
 *    are skipped, so the list shrinks live as the user fixes fields.
 *
 * Renders nothing when there is neither a message nor any outstanding error.
 */
import React from 'react'
import { Box, Link } from '@mui/material'
import NoticeBar from '../../shared/NoticeBar'

export interface FormErrorSummaryProps {
  /** field -> message (empty message = cleared, skipped). */
  errors?: Record<string, string>
  /** A server/single error message (e.g. a failed save) shown on its own. */
  message?: string | null
  /** field -> human label shown in the list (falls back to the field key). */
  fieldLabels?: Record<string, string>
  /** field -> tab index; supplying it makes the entry jump to that tab. */
  fieldToTab?: Record<string, number>
  /** field -> DOM id to scroll/focus after jumping (optional enhancement). */
  fieldAnchors?: Record<string, string>
  /** Called with the tab index when a mapped entry is clicked. */
  onJumpToTab?: (tabIndex: number) => void
  /** Override the alert heading for the field list. */
  title?: string
}

const FormErrorSummary: React.FC<FormErrorSummaryProps> = ({
  errors,
  message,
  fieldLabels,
  fieldToTab,
  fieldAnchors,
  onJumpToTab,
  title,
}) => {
  const entries = Object.entries(errors ?? {}).filter(([, msg]) => Boolean(msg))
  const showList = entries.length > 0
  if (!showList && !message) return null

  const focusAnchor = (anchorId: string) => {
    // Defer until after the tab switch renders the field.
    requestAnimationFrame(() => {
      const el = document.getElementById(anchorId)
      if (!el) return
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      if (typeof (el as HTMLElement).focus === 'function') (el as HTMLElement).focus()
    })
  }

  const handleJump = (field: string) => {
    const tab = fieldToTab?.[field]
    if (tab !== undefined) onJumpToTab?.(tab)
    const anchor = fieldAnchors?.[field]
    if (anchor) focusAnchor(anchor)
  }

  const isClickable = (field: string) =>
    fieldToTab?.[field] !== undefined || Boolean(fieldAnchors?.[field])

  const heading =
    title ?? `Please fix the following ${entries.length === 1 ? 'issue' : `${entries.length} issues`}:`

  return (
    <NoticeBar severity="error" sx={{ mb: 3 }} title={showList ? heading : undefined}>
      {message ? <Box sx={{ mb: showList ? 1 : 0 }}>{message}</Box> : null}
      {showList ? (
        <Box component="ul" sx={{ m: 0, mt: '2px', pl: 2.5, listStyleType: "'- '" }}>
          {entries.map(([field, msg]) => {
            const label = fieldLabels?.[field] ?? field
            return (
              <Box component="li" key={field} sx={{ mb: 0.25 }}>
                {isClickable(field) ? (
                  <Link
                    component="button"
                    type="button"
                    onClick={() => handleJump(field)}
                    sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                  >
                    {label}
                  </Link>
                ) : (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {label}
                  </Box>
                )}
                {` - ${msg}`}
              </Box>
            )
          })}
        </Box>
      ) : null}
    </NoticeBar>
  )
}

export default FormErrorSummary
